import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ERROR_MESSAGES } from '../components/errors/messages'
import { useGetWellExportReportData, usePollForExportReportDownload } from '../hooks/use-api'
import fetchApi from '../services/api'
import useToastMessage from './use-toast-message'
import useUser from './use-user'
import { createToastMessage, ToastMessage } from '@msaf/core-react'
import handleAPIError from '../components/errors/handler'

export default function useWellExport() {
  const Location = useLocation()
  const [wellId, setWellId] = useState<string | null>(null)
  const [exportId, setExportId] = useState<string>()
  const [isDownloadError, setIsDownloadError] = useState<boolean>()
  const [fileData, setFileData] = useState({ url: undefined, name: undefined })
  const { user } = useUser()

  const { isLoading, isError, isSuccess, data, error } = useGetWellExportReportData(
    `wells/${wellId}/request-export-report/view`,
    wellId,
  )

  const {
    isLoading: isPolling,
    isError: isPollError,
    error: pollError,
    data: pollData,
    isSuccess: isPollSuccess,
  } = usePollForExportReportDownload(`wells/${wellId}/request-export-report/${exportId}`, wellId, exportId)

  const requestDownload = async () => {
    try {
      if (user?.token) {
        const response = await fetchApi({
          token: user.token,
          auth_method: user.auth_method,
          url: `wells/export-report/${exportId}/download`,
        })

        if (response?.name && response?.url) {
          setFileData({ url: response.url, name: response.name })
        }
      }
    } catch (error: any) {
      handleAPIError(error, { componentStack: 'useWellExport' }, user)
      setIsDownloadError(true)
    }
  }

  const downloadFile = () => {
    const a = document.createElement('a')
    if (fileData.name && fileData.url) {
      a.href = fileData.url
      a.download = fileData.name
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  useEffect(() => {
    if (data?.wellExportId) {
      setExportId(data.wellExportId)
    }
  }, [data, data?.wellExportId])

  useEffect(() => {
    if (pollData?.storageFilename && exportId) {
      requestDownload()
      // Terminate Polling
      setWellId(null)
      setExportId(undefined)
    }

    if (isPollError) {
      // Terminate Polling
      setWellId(null)
      setExportId(undefined)
    }
    // Disable due to suggestion of requestDownload which cause infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollData, pollData?.storageFilename, isPollError])

  const wellExport = () => {
    const id = getWellId()
    setWellId(id)
  }

  const getWellId = () => {
    const uri = Location.pathname
    const segments = uri.split('/')
    if (segments.length > 2 && !isNaN(Number(segments[2]))) {
      return segments[2]
    } else {
      return null
    }
  }

  useEffect(() => {
    if (fileData.name && fileData.url) {
      createToastMessage(
        <ToastMessage
          messageType='info'
          title='PDF export available'
          actions={[{ label: 'Download', onClick: () => downloadFile() }]}
        />,
        { autoClose: false, closeOnClick: false },
      )
    }
    // Disable due to suggestion of downloadFile which cause infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData.name, fileData.url])

  // Request export messages
  useToastMessage({
    error: error,
    errorMessage: ERROR_MESSAGES.WELL_EXPORT_REQUEST_FAILED,
    isSuccess: isSuccess && isPollSuccess,
    successMessage: 'PDF Export of well has been requested, your download will be available shortly',
    isError: isError,
  })

  // Polling messages
  useToastMessage({
    error: isPollError,
    errorMessage: ERROR_MESSAGES.POLLING_ERROR,
    isError: isPollError,
  })

  // Download error
  useToastMessage({
    error: isDownloadError,
    errorMessage: ERROR_MESSAGES.PDF_DOWNLOAD_FAILED,
    isError: isDownloadError,
  })

  return {
    getWellId,
    wellExport,
    isLoading,
    isPolling,
    isError: isError || isPollError,
    isSuccess,
    data,
    error: error || pollError,
  }
}
